/**
 * Take the picture, and take exactly the picture that was shown.
 *
 * This started as arithmetic. The preview filled its box with `object-fit:
 * cover`, so it cropped whatever the stream's aspect ratio did not fit, while
 * the capture drew the whole frame — the photo contained strips down each side
 * that were never on screen. The first fix computed the cover crop and drew only
 * that rectangle. It was correct on paper, passed its tests, and still did not
 * match on a real phone.
 *
 * Rather than keep chasing the discrepancy — `videoWidth` against a rotated
 * display, `clientWidth` at the moment of a paused element, whatever iOS decides
 * a composited video layer measures as — the preview now shows the entire frame
 * with `object-fit: contain` and this sends the entire frame. There is nothing
 * left to compute and therefore nothing left to get wrong: what is on screen is
 * what is uploaded, by construction.
 *
 * The price is letterboxing, which is honest. It also nudges toward better
 * photographs: seeing the true edges of the frame is what tells you the panel is
 * a quarter of it and you should move closer, which matters more to the reader
 * than any crop we could have applied for you.
 */

/** The largest edge we send. Beyond this is data cost for no OCR benefit. */
const MAX_EDGE = 2000;

export interface Capture {
  blob: Blob;
  /** The same pixels, for freezing the viewfinder. */
  dataUrl: string;
}

export async function captureVisible(video: HTMLVideoElement): Promise<Capture | null> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;

  // Only a size limit, never a crop. A crop is what put the photo and the
  // preview out of step in the first place.
  const shrink = Math.min(1, MAX_EDGE / Math.max(width, height));

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * shrink);
  canvas.height = Math.round(height * shrink);

  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.9),
  );
  if (!blob) return null;

  return { blob, dataUrl: canvas.toDataURL("image/jpeg", 0.8) };
}

/**
 * A frame, as an image, and nothing else.
 *
 * `captureVisible` above is the *photograph*: it produces the bytes that get
 * uploaded, which means a blob, which means `toBlob`, which is asynchronous.
 * This one is for the thumbnail beside a barcode hit — no upload, nothing read
 * off it, just evidence that the packet on screen is the packet in the answer.
 *
 * Synchronous and total on purpose. It sits on the barcode loop's success path,
 * where an `await` on a callback the browser may never fire would hold the
 * confirm state behind a decoration: jsdom does exactly that, and a browser
 * with canvas disabled would too. A missing thumbnail is a smaller loss than a
 * scan that read the code and then appeared to hang.
 */
export function frameImage(video: HTMLVideoElement): string | null {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;

  const shrink = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * shrink);
  canvas.height = Math.round(height * shrink);

  try {
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.8);
  } catch {
    return null;
  }
}
